import { useState, useEffect, useRef } from "react";
import { supabase, isSupabaseEnabled } from "../../lib/supabase";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PropertyDoc {
  id: string;
  filename: string;
  file_path: string;
  file_size: number | null;
  category: string;
  created_at: string;
  fy_year?: string | null;
  expense_category?: string | null;
  actual_amount?: number | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  "Receipt",
  "Invoice",
  "Depreciation Report",
  "Lease",
  "Insurance",
  "Other",
] as const;

const ACCEPTED_MIME = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
];

const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatBytes(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDocDate(isoStr: string): string {
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function sanitiseFilename(name: string): string {
  const ext = name.split(".").pop() ?? "bin";
  const base = name
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-zA-Z0-9_\-]/g, "_")
    .slice(0, 60);
  return `${base}_${Date.now()}.${ext}`;
}

function categoryColour(cat: string): string {
  switch (cat) {
    case "Receipt": return "bg-emerald-100 text-emerald-700";
    case "Invoice": return "bg-blue-100 text-blue-700";
    case "Depreciation Report": return "bg-purple-100 text-purple-700";
    case "Lease": return "bg-amber-100 text-amber-700";
    case "Insurance": return "bg-rose-100 text-rose-700";
    default: return "bg-neutral-100 text-neutral-600";
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PropertyDocuments({
  propertyId,
  userId,
  fyYear,
  expenseCategory,
  actualAmount,
  onChange,
}: {
  propertyId: string;
  userId: string;
  /**
   * When set together with expenseCategory, the component becomes "row-scoped":
   * - Fetch filters to (property_id, fy_year, expense_category)
   * - Upload writes fy_year, expense_category, actual_amount columns
   * - Default category changes from "Other" to "Receipt"
   */
  fyYear?: string;
  expenseCategory?: string;
  /**
   * Audit-trail snapshot of the actual $ amount at time of upload. Written
   * once on insert; never updated if the underlying actual cell changes later.
   */
  actualAmount?: number | null;
  /**
   * Called after a successful upload or delete. Parent can use this to
   * refresh a receipt-count map.
   */
  onChange?: () => void;
}) {
  const rowScoped = Boolean(fyYear && expenseCategory);

  const [docs, setDocs] = useState<PropertyDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pending, setPending] = useState<{ file: File; category: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isSupabaseEnabled() || !propertyId) {
      setLoading(false);
      return;
    }
    fetchDocs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, fyYear, expenseCategory]);

  async function fetchDocs() {
    setLoading(true);
    let query = supabase!
      .from("property_documents")
      .select("*")
      .eq("property_id", propertyId);
    if (rowScoped) {
      query = query.eq("fy_year", fyYear!).eq("expense_category", expenseCategory!);
    }
    const { data, error } = await query.order("created_at", { ascending: false });
    if (!error && data) setDocs(data as PropertyDoc[]);
    setLoading(false);
  }

  function handleFileSelect(files: FileList | null) {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!ACCEPTED_MIME.includes(file.type)) {
      setUploadError("Only PDF, JPG, PNG, and HEIC files are accepted.");
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setUploadError("File size must be under 10 MB.");
      return;
    }
    setUploadError(null);
    setPending({ file, category: rowScoped ? "Receipt" : "Other" });
  }

  async function confirmUpload() {
    if (!pending || !isSupabaseEnabled()) return;
    setUploading(true);
    setUploadError(null);

    const filename = sanitiseFilename(pending.file.name);
    const filePath = `${userId}/${propertyId}/${filename}`;

    const { error: storageError } = await supabase!.storage
      .from("property-documents")
      .upload(filePath, pending.file, { upsert: false });

    if (storageError) {
      setUploadError("Upload failed: " + storageError.message);
      setUploading(false);
      return;
    }

    const insertPayload: Record<string, unknown> = {
      property_id: propertyId,
      user_id: userId,
      filename: pending.file.name,
      file_path: filePath,
      file_size: pending.file.size,
      category: pending.category,
    };
    if (rowScoped) {
      insertPayload.fy_year = fyYear;
      insertPayload.expense_category = expenseCategory;
      // Audit-trail snapshot of the actual amount at time of upload.
      // Never overwritten later if the actual cell changes.
      if (actualAmount !== undefined && actualAmount !== null) {
        insertPayload.actual_amount = actualAmount;
      }
    }

    const { error: dbError } = await supabase!.from("property_documents").insert(insertPayload);

    if (dbError) {
      setUploadError("Failed to save document: " + dbError.message);
      // Best-effort cleanup
      await supabase!.storage.from("property-documents").remove([filePath]);
    } else {
      setPending(null);
      await fetchDocs();
      onChange?.();
    }
    setUploading(false);
  }

  async function handleDownload(doc: PropertyDoc) {
    if (!isSupabaseEnabled()) return;
    const { data, error } = await supabase!.storage
      .from("property-documents")
      .createSignedUrl(doc.file_path, 3600);
    if (error || !data?.signedUrl) return;
    window.open(data.signedUrl, "_blank");
  }

  async function handleDelete(doc: PropertyDoc) {
    if (!isSupabaseEnabled()) return;
    setDeleting(doc.id);
    await supabase!.storage.from("property-documents").remove([doc.file_path]);
    await supabase!.from("property_documents").delete().eq("id", doc.id);
    setDeleteConfirm(null);
    setDeleting(null);
    await fetchDocs();
    onChange?.();
  }

  if (!isSupabaseEnabled()) {
    return (
      <p className="text-sm text-neutral-400">
        Document storage requires a Supabase connection.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Drop zone ─────────────────────────────────────────────────────── */}
      {!pending && (
        <div
          className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors cursor-pointer ${
            dragOver
              ? "border-[#C47A2F] bg-[#C47A2F]/5"
              : "border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFileSelect(e.dataTransfer.files);
          }}
        >
          <svg className="h-8 w-8 text-neutral-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
          <p className="text-sm font-medium text-neutral-600">
            Drop a file here or <span className="text-[#C47A2F]">click to browse</span>
          </p>
          <p className="text-xs text-neutral-400">PDF, JPG, PNG, HEIC · max 10 MB</p>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>
      )}

      {/* ── Pending file confirmation ─────────────────────────────────────── */}
      {pending && (
        <div className="rounded-xl border border-[#C47A2F]/30 bg-[#C47A2F]/5 px-4 py-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-neutral-800">{pending.file.name}</p>
              <p className="text-xs text-neutral-400">{formatBytes(pending.file.size)}</p>
            </div>
            <button
              onClick={() => { setPending(null); setUploadError(null); }}
              className="ml-3 shrink-0 text-neutral-400 hover:text-neutral-600"
            >
              ✕
            </button>
          </div>
          <div className="flex items-center gap-3">
            <select
              className="flex-1 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C47A2F]/40"
              value={pending.category}
              onChange={(e) => setPending((p) => p ? { ...p, category: e.target.value } : p)}
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button
              onClick={confirmUpload}
              disabled={uploading}
              className="rounded-xl bg-neutral-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-50"
            >
              {uploading ? "Uploading…" : "Upload"}
            </button>
          </div>
        </div>
      )}

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {uploadError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{uploadError}</p>
      )}

      {/* ── Document list ─────────────────────────────────────────────────── */}
      {loading ? (
        <p className="text-sm text-neutral-400">Loading documents…</p>
      ) : docs.length === 0 ? (
        <p className="text-sm text-neutral-400">
          No documents yet. Upload receipts, invoices, and reports to build your audit trail.
        </p>
      ) : (
        <div className="divide-y divide-neutral-100 rounded-xl border border-neutral-100 overflow-hidden">
          {docs.map((doc) => (
            <div key={doc.id} className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50">
              {/* File icon */}
              <div className="shrink-0 text-neutral-300">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
              </div>

              {/* Info */}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-neutral-800">{doc.filename}</p>
                <p className="text-xs text-neutral-400">
                  {formatDocDate(doc.created_at)}{doc.file_size ? ` · ${formatBytes(doc.file_size)}` : ""}
                </p>
              </div>

              {/* Category badge */}
              <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${categoryColour(doc.category)}`}>
                {doc.category}
              </span>

              {/* Actions */}
              <div className="shrink-0 flex items-center gap-1.5">
                <button
                  onClick={() => handleDownload(doc)}
                  className="rounded-lg border border-neutral-200 px-2.5 py-1 text-xs text-neutral-600 hover:bg-neutral-100"
                  title="Download"
                >
                  ↓
                </button>
                {deleteConfirm === doc.id ? (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleDelete(doc)}
                      disabled={deleting === doc.id}
                      className="rounded-lg bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-500 disabled:opacity-50"
                    >
                      {deleting === doc.id ? "…" : "Confirm"}
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="rounded-lg border border-neutral-200 px-2 py-1 text-xs text-neutral-500 hover:bg-neutral-100"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteConfirm(doc.id)}
                    className="rounded-lg border border-neutral-200 px-2.5 py-1 text-xs text-neutral-400 hover:bg-red-50 hover:border-red-200 hover:text-red-500"
                    title="Delete"
                  >
                    🗑
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
